import {
  Component,
  computed,
  inject,
  QueryList,
  ViewChildren,
  PLATFORM_ID, // 1. Import Platform ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Page } from '../../page/page';
import { SurveyRenderControllerStore } from '../../render-store/render.store';
import { UnifiedSurveyStore } from '../../render-store/unified-survey.store';
import { InteractionTypes } from '@hyper-inferno/survey-elements-core-utils';
import { evaluateManifest } from '../../utils/conditonal-display.evaluator';
import { PluginHost } from '../../plugin-host/plugin-host';
import { SINGLE_SURVEY_AUDIO_PLUGIN } from './pa-plugin';
import { page_timer_plugin } from './page-timer-plugin';
import { nsec } from './nsec-plugin';

@Component({
  selector: 'lib-survey-page',
  imports: [Page, PluginHost],
  templateUrl: './survey-page.html',
  styleUrl: './survey-page.css',
})
export class SurveyPage {
  // Injected instances
  surveyRenderStore = inject(SurveyRenderControllerStore);
  surveyBotController = inject(UnifiedSurveyStore);

  // Inject platform ID to detect web/mobile execution environment
  private platformId = inject(PLATFORM_ID);

  @ViewChildren('pluginRef') plugins!: QueryList<PluginHost>;

  // Use the single unified survey audio plugin across the survey
  readonly pluginsList = computed(() => {
    const currentPage = this.surveyRenderStore.currentPageState()?.page;
    if (!currentPage) return [];

    // 2. Check if it should be included based on your mobile condition
    const isMobile = this.checkIfMobile();

    const plugins = [
      page_timer_plugin,
      nsec,
      //  ans_filtering
    ];

    if (isMobile) {
      plugins.unshift(SINGLE_SURVEY_AUDIO_PLUGIN); // or .push(), depending on order
    }

    return plugins;
  });

  /**
   * Evaluates whether the current environment is a mobile / Flutter WebView environment.
   */
  private checkIfMobile(): boolean {
    // Strategy A: Check for global Flutter bridge / webview environment
    if (
      (typeof window !== 'undefined' && (window as any).flutter_inappwebview) ||
      (window as any).webkit?.messageHandlers?.flutter
    ) {
      return true;
    }

    // Strategy B: Fallback to checking if it is a browser environment (if it renders via capacitor/ionic etc.)
    // return isPlatformBrowser(this.platformId);

    return false;
  }

  onPageAction($event: { type: InteractionTypes; payload?: any }) {
    switch ($event.type) {
      case InteractionTypes.SUBMIT:
        return this.onSubmit();
      case InteractionTypes.SWITCH:
        return this.surveyBotController.onSwitchQuestionClick(
          $event.payload ?? false,
        );
      default:
        break;
    }
  }

  onSubmit() {
    this.surveyRenderStore.attemptSubmit();

    const pageElements =
      this.surveyRenderStore.currentPageState?.().page?.elements ?? [];
    const registry = this.surveyRenderStore.registry();

    let pageValid = true;
    let firstInvalidId: string | null = null; // Track the first failure

    pageElements.forEach((el) => {
      const id = String(el.id);
      const entry = registry[id];

      // 1. CHECK VISIBILITY FIRST
      const isVisible = el.conditional_display_manifest
        ? evaluateManifest(el.conditional_display_manifest, registry)
        : true;

      if (!isVisible) {
        return; // Skip hidden elements
      }

      // 2. VALIDATE ONLY VISIBLE ELEMENTS
      if (entry && typeof entry.validate === 'function') {
        const isElementValid = entry.validate();
        if (!isElementValid) {
          pageValid = false;

          // Capture the first invalid element ID to scroll to it
          if (!firstInvalidId) {
            firstInvalidId = id;
          }
        }
      }
    });

    this.plugins.forEach((p) => p.runSubmitHook());

    if (pageValid) {
      // SUCCESS: Scroll to top and proceed
      this.scrollToTop();
      this.surveyBotController.onSubmitButtonClick();
    } else if (firstInvalidId) {
      // FAILURE: Scroll to the specific invalid question
      this.scrollToElement(firstInvalidId);
    }
  }

  private scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private scrollToElement(elementId: string) {
    // We look for the element in the DOM.
    // Ensure your question components have [id]="element.id" in their HTML.
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}
